import type { Summary } from './types';

function toNumberArray(values?: Array<number | string>): number[] {
  if (!Array.isArray(values)) return [];
  const out: number[] = [];
  for (const v of values) {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) out.push(n);
  }
  return out;
}

function quantile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const pos = (sorted.length - 1) * p;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

export function renderBoxPlot(summary: Summary): HTMLElement {
  const container = document.createElement('div');
  container.className = 'chart chart--box';

  const values = toNumberArray(summary.values);
  if (!values.length) return container;

  const data = values.slice().sort((a, b) => a - b);
  const min = data[0];
  const max = data[data.length - 1];
  const q1 = quantile(data, 0.25);
  const median = quantile(data, 0.5);
  const q3 = quantile(data, 0.75);

  const width = Math.max(320, container.clientWidth || 640);
  const height = 260;
  const margin = { top: 24, right: 24, bottom: 36, left: 24 };
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));

  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('transform', `translate(${margin.left},${margin.top})`);
  svg.appendChild(g);

  // Scale
  const rng = max - min || 1;
  const x = (v: number) => ((v - min) / rng) * innerW;

  // Axis line
  const axis = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  axis.setAttribute('x1', '0');
  axis.setAttribute('y1', String(innerH / 2));
  axis.setAttribute('x2', String(innerW));
  axis.setAttribute('y2', String(innerH / 2));
  axis.setAttribute('stroke', 'rgba(148,163,184,0.6)');
  axis.setAttribute('stroke-width', '1');
  g.appendChild(axis);

  // Whiskers
  const whiskerY = innerH / 2;
  const whisker = (x1: number, x2: number) => {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(whiskerY));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(whiskerY));
    line.setAttribute('stroke', '#475569');
    line.setAttribute('stroke-width', '2');
    return line;
  };
  g.appendChild(whisker(x(min), x(q1)));
  g.appendChild(whisker(x(q3), x(max)));

  // Whisker caps
  const cap = (cx: number) => {
    const capLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    capLine.setAttribute('x1', String(cx));
    capLine.setAttribute('y1', String(whiskerY - 12));
    capLine.setAttribute('x2', String(cx));
    capLine.setAttribute('y2', String(whiskerY + 12));
    capLine.setAttribute('stroke', '#475569');
    capLine.setAttribute('stroke-width', '2');
    return capLine;
  };
  g.appendChild(cap(x(min)));
  g.appendChild(cap(x(max)));

  // Box (Q1 to Q3)
  const box = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  box.setAttribute('x', String(Math.min(x(q1), x(q3))));
  box.setAttribute('y', String(whiskerY - 24));
  box.setAttribute('width', String(Math.abs(x(q3) - x(q1))));
  box.setAttribute('height', String(48));
  box.setAttribute('fill', 'rgba(37,99,235,0.15)');
  box.setAttribute('stroke', 'rgba(37,99,235,0.55)');
  box.setAttribute('stroke-width', '2');
  g.appendChild(box);

  // Median line
  const med = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  med.setAttribute('x1', String(x(median)));
  med.setAttribute('y1', String(whiskerY - 24));
  med.setAttribute('x2', String(x(median)));
  med.setAttribute('y2', String(whiskerY + 24));
  med.setAttribute('stroke', '#2563EB');
  med.setAttribute('stroke-width', '3');
  g.appendChild(med);

  // Labels (min / Q1 / median / Q3 / max)
  const label = (vx: number, text: string, dy = 0) => {
    const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    t.setAttribute('x', String(x(vx)));
    t.setAttribute('y', String(innerH - 4 + dy));
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('fill', '#475569');
    t.setAttribute('font-size', '12');
    t.textContent = text;
    return t;
  };
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  g.appendChild(label(min, fmt(min)));
  g.appendChild(label(q1, fmt(q1)));
  g.appendChild(label(median, fmt(median), -12));
  g.appendChild(label(q3, fmt(q3)));
  g.appendChild(label(max, fmt(max)));

  container.appendChild(svg);
  return container;
}

